import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
    createAdhocProviderInvitation,
    deleteAdhocProviderInvitation,
    getAdhocProviderInvitation,
    type AdhocProviderInvitationPayload,
    type GetAdhocProviderInvitationParams,
} from '../../services';
import { providerCandidateKeys } from './useProviderCandidates';

export const providerInvitationKeys = {
    all: ['providerInvitations'] as const,
    adhoc: (params?: GetAdhocProviderInvitationParams) =>
        [...providerInvitationKeys.all, 'adhoc', params ? { ...params } : undefined] as const,
};

/**
 * Requires at least one of `alert`/`provider_org` to actually run — a bare
 * `useAdhocProviderInvitation()` (or one called with neither filter set)
 * must NOT fall through to fetching the unfiltered invitation list.
 */
export function useAdhocProviderInvitation(
    params?: GetAdhocProviderInvitationParams,
) {
    return useQuery({
        queryKey: providerInvitationKeys.adhoc(params),
        queryFn: () => getAdhocProviderInvitation(params),
        enabled: !!(params?.alert || params?.provider_org),
    });
}

export function useCreateAdhocProviderInvitation() {
    const queryClient = useQueryClient();
    return useMutation({
        mutationFn: (payload: AdhocProviderInvitationPayload) =>
            createAdhocProviderInvitation(payload),
        onSuccess: () => {
            queryClient.invalidateQueries({
                queryKey: providerInvitationKeys.all,
            });
            queryClient.invalidateQueries({
                queryKey: providerCandidateKeys.all,
            });
        },
    });
}

export function useDeleteAdhocProviderInvitation() {
    const queryClient = useQueryClient();
    return useMutation({
        mutationFn: (id: string | number) =>
            deleteAdhocProviderInvitation(id),
        onSuccess: () => {
            queryClient.invalidateQueries({
                queryKey: providerInvitationKeys.all,
            });
            queryClient.invalidateQueries({
                queryKey: providerCandidateKeys.all,
            });
        },
    });
}

